import { createSlice, createAsyncThunk, PayloadAction, current } from '@reduxjs/toolkit';
import type { ProjectData, ProjectMetaData, GetProjectsParams, CreateProjectParams, ProjectsResponse, DeleteProjectResponse, ProjectMode } from '../../types/project.types';
import apiClient from '../../api/client';
import { ApiUrls } from '../../api/ApiUrls';
import { projectService } from '../../api/services/project.service';
import type { ScopeDto } from '../../api/services/project.service';

export interface ProjectConversation {
  id: string;
  name?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
  mode?: string;
  [key: string]: any;
}

export interface ProjectSliceState {
  projects: ProjectData[];
  projectMetaData: ProjectMetaData | null;
  metadataConversationId: string | null;
  conversations: ProjectConversation[];
  scopes: ScopeDto[];
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  isFetchingSingle: boolean;
  isFetchingConversations: boolean;
  isFetchingScopes: boolean;
  error: string | null;
  conversationsError: string | null;
}

const initialState: ProjectSliceState = {
  projects: [],
  projectMetaData: null,
  metadataConversationId: null,
  conversations: [],
  scopes: [],
  isLoading: false,
  isCreating: false,
  isDeleting: false,
  isUpdating: false,
  isFetchingSingle: false,
  isFetchingConversations: false,
  isFetchingScopes: false,
  error: null,
  conversationsError: null,
};

// Async thunk for fetching projects
export const getUserProjectsWithPagination = createAsyncThunk<
  ProjectsResponse,
  GetProjectsParams
>(
  'project/getWithPagination',
  async (params, { rejectWithValue }) => {
    try {
      const url = ApiUrls.getUserProjectsWithPagination(params.limit, params.page);
      const { data } = await apiClient.get(url);
      
      // API returns array directly or object with data property
      const projects = Array.isArray(data) ? data : (data.data || []);
      
      return {
        data: projects,
        total: data.total || projects.length,
        page: params.page,
        limit: params.limit,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch projects');
    }
  }
);

// Async thunk for creating a project
export const createProject = createAsyncThunk<
  { project_id: string; data: ProjectData },
  CreateProjectParams
>(
  'project/create',
  async (params, { rejectWithValue }) => {
    try {
      const requestBody = {
        name: params.name,
        description: params.description,
        ideoz_role: params.ideoz_role,
        main_persona: params.main_persona,
        industry: params.industry,
      };
      const { data } = await apiClient.post(ApiUrls.createProject, requestBody);
      
      // API returns { project_id, data: projectData } or just projectData
      const projectId = data.project_id || data.data?.id || data.id;
      const projectData = data.data || data;
      
      return {
        project_id: projectId,
        data: projectData,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create project');
    }
  }
);

// Async thunk for deleting a project
export const deleteProject = createAsyncThunk<
  DeleteProjectResponse,
  string
>(
  'project/delete',
  async (projectId, { rejectWithValue }) => {
    try {
      await apiClient.delete(ApiUrls.deleteProject(projectId));
      return {
        success: true,
        projectId,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete project');
    }
  }
);

// Async thunk for fetching a single project
export const getProject = createAsyncThunk<
  ProjectData,
  string
>(
  'project/getOne',
  async (projectId, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.get(ApiUrls.getProject(projectId));
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch project');
    }
  }
);

// Async thunk for updating a project
export const updateProject = createAsyncThunk<
  ProjectData,
  { projectId: string; projectData: any }
>(
  'project/update',
  async ({ projectId, projectData }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.patch(ApiUrls.updateProject(projectId), projectData);
      return data?.data || data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update project');
    }
  }
);

// Async thunk for fetching project conversations
export const getProjectConversations = createAsyncThunk<
  ProjectConversation[],
  { projectId: string; mode?: ProjectMode }
>(
  'project/getConversations',
  async ({ projectId, mode }, { rejectWithValue }) => {
    try {
      const url = ApiUrls.projectConversations(projectId);
      const params = mode ? { mode } : {};
      const { data } = await apiClient.get(url, { params });
      
      // API might return array directly or object with data property
      const conversations = Array.isArray(data) ? data : (data.data || data.conversations || []);
      
      return conversations;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch conversations');
    }
  }
);

// Async thunk for fetching scopes under a startup
export const getStartupScopes = createAsyncThunk<
  ScopeDto[],
  { startupId: string }
>(
  'project/getStartupScopes',
  async ({ startupId }, { rejectWithValue }) => {
    try {
      return await projectService.getStartupScopes(startupId);
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to fetch scopes');
    }
  },
);

// Async thunk for updating project mode (state only, no API call)
export const updateProjectMode = createAsyncThunk<
  { projectId: string; mode: ProjectMode },
  { projectId: string; mode: ProjectMode }
>(
  'project/updateMode',
  async ({ projectId, mode }) => {
    // Update state only, no API call
    return { projectId, mode };
  }
);

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setProjectMetaData: (state, action: PayloadAction<ProjectMetaData | null>) => {
      state.projectMetaData = action.payload;
    },
    setMetadataConversationId: (state, action: PayloadAction<string | null>) => {
      state.metadataConversationId = action.payload;
    },
    setProjectMode: (state, action: PayloadAction<{ projectId: string; mode: ProjectMode }>) => {
      const project = state.projects.find((p: ProjectData) => p.id === action.payload.projectId);
      if (project) {
        project.mode = action.payload.mode;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Optimistically prepend a just-created scope so the list reflects it
     * instantly, without waiting for the refetch round-trip. Deduped by id so a
     * subsequent refetch (or a double dispatch) can't insert it twice.
     */
    addScope: (state, action: PayloadAction<ScopeDto>) => {
      const scope = action.payload;
      if (!scope?.id || state.scopes.some((s: ScopeDto) => s.id === scope.id)) return;
      state.scopes.unshift(scope);
    },
    clearConversations: (state) => {
      state.conversations = [];
      state.conversationsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get projects
      .addCase(getUserProjectsWithPagination.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProjectsWithPagination.fulfilled, (state, action) => {
        state.projects = action.payload.data.map((project: ProjectData) => ({
          ...project,
          mode: project.mode || 'clarify',
        }));
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getUserProjectsWithPagination.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create project
      .addCase(createProject.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        const newProject = {
          ...action.payload.data,
          mode: action.payload.data.mode || 'clarify',
        };
        state.projects.unshift({...newProject, id: action.payload.project_id || newProject.id });
        state.isCreating = false;
        state.error = null;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Delete project
      .addCase(deleteProject.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter(
          (p: ProjectData) => p.id !== action.payload.projectId
        );
        state.isDeleting = false;
        state.error = null;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Get single project
      .addCase(getProject.pending, (state) => {
        state.isFetchingSingle = true;
        state.error = null;
      })
      .addCase(getProject.fulfilled, (state, action) => {
        // Add or update project in the list
        const projectWithMode = {
          ...action.payload,
          mode: action.payload.mode || 'clarify',
        };
        const existingIndex = state.projects.findIndex((p: ProjectData) => p.id === action.payload.id);
        if (existingIndex >= 0) {
          state.projects[existingIndex] = projectWithMode;
        } else {
          state.projects.unshift(projectWithMode);
        }
        state.isFetchingSingle = false;
        state.error = null;
      })
      .addCase(getProject.rejected, (state, action) => {
        state.isFetchingSingle = false;
        state.error = action.payload as string;
      })
      // Update project
      .addCase(updateProject.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        // Update project in the list
        const index = state.projects.findIndex((p: ProjectData) => p.id === action.payload.id);
        if (index >= 0) {
          state.projects[index] = { ...state.projects[index], ...action.payload, hasUpdateDetails: true};
        }
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Get project conversations
      .addCase(getProjectConversations.pending, (state) => {
        state.isFetchingConversations = true;
        state.conversationsError = null;
      })
      .addCase(getProjectConversations.fulfilled, (state, action) => {
        state.conversations = action.payload;
        state.isFetchingConversations = false;
        state.conversationsError = null;
      })
      .addCase(getProjectConversations.rejected, (state, action) => {
        state.isFetchingConversations = false;
        state.conversationsError = action.payload as string;
      })
      // Get startup scopes
      .addCase(getStartupScopes.pending, (state) => {
        state.isFetchingScopes = true;
      })
      .addCase(getStartupScopes.fulfilled, (state, action) => {
        state.isFetchingScopes = false;
        state.scopes = action.payload;
      })
      .addCase(getStartupScopes.rejected, (state) => {
        state.isFetchingScopes = false;
      })
      // Update project mode (state only, no API call)
      .addCase(updateProjectMode.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProjectMode.fulfilled, (state, action) => {
        const project = state.projects.find((p: ProjectData) => p.id === action.payload.projectId);
        if (project) {
          project.mode = action.payload.mode;
        }
        state.isUpdating = false;
        state.error = null;
      })
      .addCase(updateProjectMode.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const { setProjectMetaData, setMetadataConversationId, setProjectMode, clearError, clearConversations, addScope } = projectSlice.actions;

// Selectors with defensive checks
export const selectProjects = (state: { project: ProjectSliceState }) => state.project?.projects ?? [];
export const selectProjectMetaData = (state: { project: ProjectSliceState }) => state.project?.projectMetaData ?? null;
export const selectMetadataConversationId = (state: { project: ProjectSliceState }) => state.project?.metadataConversationId ?? null;
export const selectIsLoadingProjects = (state: { project: ProjectSliceState }) => state.project?.isLoading ?? false;
export const selectIsCreatingProject = (state: { project: ProjectSliceState }) => state.project?.isCreating ?? false;
export const selectIsDeletingProject = (state: { project: ProjectSliceState }) => state.project?.isDeleting ?? false;
export const selectIsUpdatingProject = (state: { project: ProjectSliceState }) => state.project?.isUpdating ?? false;
export const selectIsFetchingSingleProject = (state: { project: ProjectSliceState }) => state.project?.isFetchingSingle ?? false;
export const selectProjectError = (state: { project: ProjectSliceState }) => state.project?.error ?? null;
export const selectCurrentProject = (state: { project: ProjectSliceState }, currentProjectId: string | null) => 
  state.project?.projects?.find((p: ProjectData) => p.id === currentProjectId);
export const selectProjectConversations = (state: { project: ProjectSliceState }) => state.project?.conversations ?? [];
export const selectIsFetchingConversations = (state: { project: ProjectSliceState }) => state.project?.isFetchingConversations ?? false;
export const selectConversationsError = (state: { project: ProjectSliceState }) => state.project?.conversationsError ?? null;
export const selectProjectScopes = (state: any) => state.project?.scopes ?? [];
export const selectIsFetchingScopes = (state: any) => state.project?.isFetchingScopes ?? false;

export default projectSlice.reducer;
